import { useEffect, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type {
  Control,
  FieldErrors,
  Path,
  UseFieldArrayReturn,
  UseFormSetValue,
} from 'react-hook-form'
import { Controller, useFieldArray, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Checkbox,
  FieldRoot,
  Grid,
  HStack,
  IconButton,
  Input,
  Stack,
  TertiaryButton,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaPlusRegular, FaXmarkRegular } from '@enechain/ecloud-icons'

import { ConfirmDialog } from '~/components/common/ConfirmDialog'
import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { FormSelect } from '~/components/common/FormSelect'
import { HelpIcon } from '~/components/common/HelpIcon'
import { RequiredMark } from '~/components/common/RequiredMark'
import { AREA_CODE_OPTIONS, convertAreaLabel } from '~/models/area'
import { ContactEntryFields } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/ContactEntryFields'
import type { TradingPartnerFormInput } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/schema'
import { areaCodeFromTrailingDigit } from '~/pages/AdminOcctoMaster/tabs/_common/models/areaCodeFromTrailingDigit'

type Props = {
  control: Control<TradingPartnerFormInput>
  errors: FieldErrors<TradingPartnerFormInput>
  setValue: UseFormSetValue<TradingPartnerFormInput>
}

/**
 * アカウント編集セクション。
 *
 * design/admin/occto.html #tp-edit-modal 準拠:
 * - sub-header はラベル + info tooltip + 右端「+ 追加」(旧: ラベルのみ + 下部全幅
 *   「アカウント追加」ボタン)
 * - 各アカウントは `.tp-acc-entry` (Surface.secondary 背景 + border) カードで、
 *   上部バーに採番見出し「アカウントN」+ 右上に corner ✕ (IconButton ghost)
 *   (旧: カード末尾の全幅「削除」ボタン)
 * - head は 3 列 grid (140px 1fr 1fr: エリア / 取引先コード / 取引先コード名称)
 *
 * D2: license を削除し Area × Code × CodeName の動的リストを管理する。
 * 各アカウント配下に平日昼間 / 休日・夜間の連絡先 (ContactEntryFields) を内包する
 * (= mock-app の取引先編集モーダルと同じ階層構造)。
 */
export const AccountSection = ({
  control,
  errors,
  setValue,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const {
    fields,
    append,
    remove,
  }: UseFieldArrayReturn<TradingPartnerFormInput, 'accounts'> = useFieldArray({
    control,
    name: 'accounts',
  })

  // AC-OMT-022: 取引先コードを 5 桁入力したら末尾 1 桁からエリアを自動選択。
  // ユーザーが明示的にエリアを変更したアカウントは上書きしない。
  // 自動選択でセットした値を記録し、以降の自動上書き許可判定に使う。
  const [autoSelectedAreas, setAutoSelectedAreas] = useState<
    Map<string, AreaCode>
  >(new Map())
  const accounts = useWatch<TradingPartnerFormInput, 'accounts'>({
    control,
    name: 'accounts',
  })
  useEffect(() => {
    if (accounts === undefined) {
      return
    }
    const updates = new Map<string, AreaCode>()
    for (const [index, account] of accounts.entries()) {
      if (account == null) {
        continue
      }
      const code = account.code ?? ''
      if (code.length !== 5) {
        continue
      }
      const trailingChar = code[code.length - 1]
      if (!/^\d$/.test(trailingChar)) {
        continue
      }
      const field = fields[index]
      if (field == null) {
        continue
      }
      const derivedAreaCode = areaCodeFromTrailingDigit(Number(trailingChar))
      if (derivedAreaCode === undefined) {
        continue
      }
      const autoArea = autoSelectedAreas.get(field.id)
      const canAuto =
        account.areaCode === AreaCode.UNSPECIFIED ||
        account.areaCode === autoArea
      if (canAuto && derivedAreaCode !== account.areaCode) {
        setValue(
          `accounts.${index}.areaCode` as Path<TradingPartnerFormInput>,
          derivedAreaCode,
          { shouldValidate: false },
        )
        updates.set(field.id, derivedAreaCode)
      }
    }
    if (updates.size > 0) {
      setAutoSelectedAreas((prev) => {
        const next = new Map(prev)
        for (const [id, area] of updates.entries()) {
          next.set(id, area)
        }
        return next
      })
    }
  }, [accounts, fields, autoSelectedAreas, setValue])
  // AC-04 (spec AC-T06): アカウント削除は即時実行せず、 確認ダイアログを経由
  // する。 削除対象の index を保持し、 確認まで `remove()` を呼ばない
  // (= キャンセルで何もせず「元に戻る」)。 [design差異] トリガーは design の corner
  // ✕ アイコンに合わせたが、 確認ダイアログの UI/挙動は変更しない (spec AC-T06 要求)。
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(
    null,
  )

  // AC-OMT-006: 確認ダイアログ本文は取引先コード (+ 名称) を含める。
  // 取引先コード名称が空欄の時は「取引先コード「{コード}」を削除しますか？」。
  const pendingRemoveAccount =
    pendingRemoveIndex === null ? undefined : accounts?.[pendingRemoveIndex]
  const pendingRemoveCode = pendingRemoveAccount?.code ?? ''
  const pendingRemoveCodeName = pendingRemoveAccount?.codeName ?? ''
  const removeAccountConfirmBody =
    pendingRemoveCodeName === ''
      ? t(
          'page:adminOcctoMaster.tradingPartner.form.removeAccountConfirmBodyNoName',
          { code: pendingRemoveCode },
        )
      : t(
          'page:adminOcctoMaster.tradingPartner.form.removeAccountConfirmBody',
          { code: pendingRemoveCode, codeName: pendingRemoveCodeName },
        )

  // AC-OMT-029: 代表 checkbox off 操作も即時実行せず、 削除件数付き確認
  // ダイアログを経由する (キャンセルで on に戻る = チェック状態は
  // isRepresentative の form 値そのものなので何もしなければ自動的に維持される)。
  const [pendingUnrepresentIndex, setPendingUnrepresentIndex] = useState<
    number | null
  >(null)
  const pendingUnrepresentAccount =
    pendingUnrepresentIndex !== null
      ? accounts?.[pendingUnrepresentIndex]
      : undefined
  // spec AC-OMT-029 の確認文言は平日昼間 / 休日・夜間を合算せず区分別に件数を
  // 明示する（「平日昼間 N 件・休日・夜間 M 件」）。
  const pendingUnrepresentWeekdayCount =
    pendingUnrepresentAccount?.weekdayContacts?.length ?? 0
  const pendingUnrepresentHolidayCount =
    pendingUnrepresentAccount?.holidayContacts?.length ?? 0

  return (
    <Box>
      {/* sub-header (design `.occto-sub-header`):
          border-top + padding-top + marginTop で直前セクション (取引先名称/略称) と
          区切り、 label + info tooltip (左) と 「+ 追加」 (右) を配置する。
          label は design `.occto-sub-label` = lg/700 に合わせ textStyle="modalSectionTitle" を使う。 */}
      <HStack
        justify="space-between"
        marginTop={4}
        marginBottom={2}
        paddingTop={3}
        borderTopWidth="1px"
        borderTopColor="Border.lowEmphasis"
      >
        <HStack gap={1}>
          {/* AC-OMT-002 [P0] が「取引先コードのエリア・取引先コード (値)」 を必須項目と
              定めており、 schema も `accounts: z.array(...).min(1)` で 0 件を弾く
              (AC-OMT-012)。 同じモーダルの 取引先名称 / 略称 は
              `FieldRequiredIndicator` で必須マークが出るのに、 ここだけ出ておらず
              必須に見えなかった (2026-08-26 デザイナー指摘)。
              このセクションは `FieldRoot` ではない (= 繰り返し入力のまとまり) ので
              DS の `FieldRequiredIndicator` は使えず、 共通の `RequiredMark` を添える。 */}
          <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
            {t('page:adminOcctoMaster.tradingPartner.form.accounts')}
            <RequiredMark />
          </Text>
          <HelpIcon
            ariaLabel={t(
              'page:adminOcctoMaster.tradingPartner.form.accountsTooltipAriaLabel',
            )}
            content={t(
              'page:adminOcctoMaster.tradingPartner.form.accountsTooltip',
            )}
          />
        </HStack>
        <TertiaryButton
          aria-label={t('page:adminOcctoMaster.tradingPartner.form.addAccount')}
          onClick={() => {
            append({
              areaCode: AreaCode.UNSPECIFIED,
              code: '',
              codeName: '',
              // AC-OMT-030: 新規コードの代表フラグ既定 true。
              isRepresentative: true,
              weekdayContacts: [],
              holidayContacts: [],
            })
          }}
          size="sm"
        >
          <FaPlusRegular />
          {t('page:adminOcctoMaster.tradingPartner.form.add')}
        </TertiaryButton>
      </HStack>
      {/* 0 件エラー (AC-OMT-012「取引先コードを1件以上登録してください」) を
          **ここには出さない**。 `accounts` は nested error (深さ 2 以上) なので
          `resolveFormErrorAlertMessage` が
          モーダル上部の Alert へ集約する = ここに出すと二重表示になる。

          2026-08-26 まで、 ここに `errors.accounts` を出す `<Text>` があったが
          **一度も描画されていなかった**。 RHF は配列 field のエラーを配列で表すため
          ガードの `!Array.isArray(errors.accounts)` が常に false になる。
          実測 (この状態のモーダル DOM) でも当該文言の出現は上部 Alert の 1 箇所だけ。
          しかも色に `Text.negative.high` という**存在しないトークン**を指定しており
          (DS の実名は negativeHigh / negativeMid / negativeLow。 app 全体でここだけ)、
          仮に描画されても chakra が生文字列 `color:Text.negative.high` を出力し、
          CSS として不正なので宣言ごと捨てられて既定色 (黒) になっていた。
          = 「エラーなのに赤くない」 死にコードだったので削除した。 */}
      {/* 空状態は**状態の記述**に徹する。 「登録してください」 系の指示は上部 Alert
          (AC-OMT-012) の役目で、 ここで言い直すと同じことを 2 度違う見た目で言うことに
          なる (2026-08-26 デザイナー指摘)。 文言は AC-OMT-017 (一覧内と同じ表記)。 */}
      {fields.length === 0 ? (
        <Text color="Text.midEmphasis" fontSize="md">
          {t('page:adminOcctoMaster.tradingPartner.form.noAccountsRegistered')}
        </Text>
      ) : (
        <Stack gap={4}>
          {fields.map((field, index) => (
            <Box
              key={field.id}
              // 他の行カード (構成事業者 / 配下発電所 / 所属BG) と余白を揃える
              // (2026-08-26 デザイナー指摘)。 削除 ✕ は元からカード上部バーの右端に
              // あり、 カード全体に掛かる操作として読める形なので配置は変えない。
              padding={4}
              // design `.tp-acc-entry`: background=Surface.secondary + border 1px
              // (BorderLowEmphasis) + border-radius=base。 border は 6 タブの
              // `.occto-member-row` (background のみ) と違い取引先アカウントだけ
              // 明示 border を持つ (= 独立エントリを強調する意図)。
              background="Surface.secondary"
              borderWidth="1px"
              borderColor="Border.lowEmphasis"
              borderRadius="base"
            >
              <Stack gap={3}>
                {/* カード上部バー: 採番見出し + corner ✕ (design: .tp-acc-bar)。
                    design `.tp-acc-title` = lg/700。 */}
                <HStack justify="space-between">
                  <Text textStyle="modalSectionTitle" color="Text.highEmphasis">
                    {t(
                      'page:adminOcctoMaster.tradingPartner.form.accountCardTitle',
                      {
                        n: index + 1,
                      },
                    )}
                  </Text>
                  <IconButton
                    color="Text.midEmphasis"
                    aria-label={t(
                      'page:adminOcctoMaster.tradingPartner.form.removeAccount',
                    )}
                    onClick={() => {
                      setPendingRemoveIndex(index)
                    }}
                    size="sm"
                    variant="ghost"
                  >
                    <FaXmarkRegular />
                  </IconButton>
                </HStack>
                {/* areaCode + code + codeName: design の tp-acc-head = grid(140px 1fr 1fr) */}
                <Grid gridGap={3} gridTemplateColumns="140px 1fr 1fr">
                  {/* areaCode */}
                  <FieldRoot
                    alignItems="stretch"
                    invalid={errors.accounts?.[index]?.areaCode !== undefined}
                  >
                    <FieldLabel>
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.accountAreaCode',
                      )}
                    </FieldLabel>
                    {/* 「未選択」 は placeholder ではなく UNSPECIFIED (=0) の
                        選択肢として持つ (form 値は enum 必須で、 自動選択された
                        エリアを手で「未選択」へ戻せる必要があるため)。
                        onValueChange = AC-OMT-022: ユーザーが手で選び直した
                        エリアは以後、 取引先コードからの自動選択で上書きしない。 */}
                    <FormSelect
                      aria-label={t(
                        'page:adminOcctoMaster.tradingPartner.form.accountAreaCode',
                      )}
                      control={control}
                      name={`accounts.${index}.areaCode`}
                      numeric
                      onValueChange={() => {
                        setAutoSelectedAreas((prev) => {
                          const next = new Map(prev)
                          next.delete(field.id)
                          return next
                        })
                      }}
                      options={[
                        {
                          value: String(AreaCode.UNSPECIFIED),
                          label: t(
                            'page:adminOcctoMaster.tradingPartner.form.areaCodeUnselected',
                          ),
                        },
                        ...AREA_CODE_OPTIONS.map((code) => ({
                          value: String(code),
                          label: convertAreaLabel(code),
                        })),
                      ]}
                    />
                    {errors.accounts?.[index]?.areaCode !== undefined && (
                      <FieldErrorText>
                        {errors.accounts[index].areaCode?.message}
                      </FieldErrorText>
                    )}
                  </FieldRoot>
                  {/* code */}
                  <Controller
                    control={control}
                    name={`accounts.${index}.code`}
                    render={({ field }) => (
                      <FieldRoot
                        alignItems="stretch"
                        invalid={errors.accounts?.[index]?.code !== undefined}
                      >
                        <FieldLabel htmlFor={`account-${index}-code`}>
                          {t(
                            'page:adminOcctoMaster.tradingPartner.form.accountCode',
                          )}
                        </FieldLabel>
                        <Input
                          {...field}
                          id={`account-${index}-code`}
                          maxLength={5}
                          placeholder={t(
                            'page:adminOcctoMaster.tradingPartner.form.accountCodePlaceholder',
                          )}
                        />
                        {errors.accounts?.[index]?.code !== undefined && (
                          <FieldErrorText>
                            {errors.accounts[index].code?.message}
                          </FieldErrorText>
                        )}
                      </FieldRoot>
                    )}
                  />
                  {/* codeName */}
                  <Controller
                    control={control}
                    name={`accounts.${index}.codeName`}
                    render={({ field }) => (
                      <FieldRoot
                        alignItems="stretch"
                        invalid={
                          errors.accounts?.[index]?.codeName !== undefined
                        }
                      >
                        <FieldLabel htmlFor={`account-${index}-code-name`}>
                          {t(
                            'page:adminOcctoMaster.tradingPartner.form.accountCodeName',
                          )}
                        </FieldLabel>
                        <Input
                          {...field}
                          id={`account-${index}-code-name`}
                          placeholder={t(
                            'page:adminOcctoMaster.tradingPartner.form.accountCodeNamePlaceholder',
                          )}
                        />
                        {errors.accounts?.[index]?.codeName !== undefined && (
                          <FieldErrorText>
                            {errors.accounts[index].codeName?.message}
                          </FieldErrorText>
                        )}
                      </FieldRoot>
                    )}
                  />
                </Grid>
                {/* 代表フラグ (spec AC-OMT-004/028/029/030): 連絡先を保持できるのは
                    代表アカウントのみ。 off 操作は即時反映せず削除件数付き確認
                    ダイアログを経由する (AC-OMT-029)。 */}
                <Controller
                  control={control}
                  name={`accounts.${index}.isRepresentative`}
                  render={({ field }) => (
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(details: {
                        checked: boolean | 'indeterminate'
                      }) => {
                        if (details.checked === true) {
                          field.onChange(true)
                          return
                        }
                        setPendingUnrepresentIndex(index)
                      }}
                    >
                      {t(
                        'page:adminOcctoMaster.tradingPartner.form.representative',
                      )}
                    </Checkbox>
                  )}
                />
                {accounts?.[index]?.isRepresentative === true ? (
                  <>
                    {/* 平日昼間 連絡先 */}
                    <ContactEntryFields
                      accountIndex={index}
                      control={control}
                      errors={errors}
                      kind="weekdayContacts"
                      label={t(
                        'page:adminOcctoMaster.tradingPartner.form.weekdayContacts',
                      )}
                    />
                    {/* 休日・夜間 連絡先 */}
                    <ContactEntryFields
                      accountIndex={index}
                      control={control}
                      errors={errors}
                      kind="holidayContacts"
                      label={t(
                        'page:adminOcctoMaster.tradingPartner.form.holidayContacts',
                      )}
                    />
                  </>
                ) : (
                  <Text color="Text.midEmphasis" fontSize="sm">
                    {t(
                      'page:adminOcctoMaster.tradingPartner.form.representativeGuidance',
                    )}
                  </Text>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
      {/* AC-04: アカウント削除確認ダイアログ。 確認するまで remove() を呼ばず、
          キャンセルすると何も変更されずに元へ戻る。 */}
      <ConfirmDialog
        body={removeAccountConfirmBody}
        confirmLabel={t(
          'page:adminOcctoMaster.tradingPartner.form.removeAccountConfirmLabel',
        )}
        destructive
        isOpen={pendingRemoveIndex !== null}
        onCancel={() => {
          setPendingRemoveIndex(null)
        }}
        onConfirm={() => {
          if (pendingRemoveIndex !== null) {
            remove(pendingRemoveIndex)
          }
          setPendingRemoveIndex(null)
        }}
        title={t(
          'page:adminOcctoMaster.tradingPartner.form.removeAccountConfirmTitle',
        )}
      />
      {/* AC-OMT-029: 代表解除の確認ダイアログ。 確認まで isRepresentative /
          連絡先を変更せず、 キャンセルすると checkbox は checked のまま
          (= 何も変更していないので自動的に元へ戻る)。 */}
      <ConfirmDialog
        body={t(
          'page:adminOcctoMaster.tradingPartner.form.removeRepresentativeConfirmBody',
          {
            weekdayCount: pendingUnrepresentWeekdayCount,
            holidayCount: pendingUnrepresentHolidayCount,
          },
        )}
        confirmLabel={t(
          'page:adminOcctoMaster.tradingPartner.form.removeRepresentativeConfirmLabel',
        )}
        destructive
        isOpen={pendingUnrepresentIndex !== null}
        onCancel={() => {
          setPendingUnrepresentIndex(null)
        }}
        onConfirm={() => {
          if (pendingUnrepresentIndex !== null) {
            setValue(
              `accounts.${pendingUnrepresentIndex}.isRepresentative` as Path<TradingPartnerFormInput>,
              false,
              { shouldValidate: true },
            )
            // AC-OMT-029: 代表解除に伴い連絡先を削除する (非代表アカウントは
            // 連絡先を保持できない、 schema のガーディング refine と整合)。
            setValue(
              `accounts.${pendingUnrepresentIndex}.weekdayContacts` as Path<TradingPartnerFormInput>,
              [],
              { shouldValidate: true },
            )
            setValue(
              `accounts.${pendingUnrepresentIndex}.holidayContacts` as Path<TradingPartnerFormInput>,
              [],
              { shouldValidate: true },
            )
          }
          setPendingUnrepresentIndex(null)
        }}
        title={t(
          'page:adminOcctoMaster.tradingPartner.form.removeRepresentativeConfirmTitle',
        )}
      />
    </Box>
  )
}
